import { useEffect, useState } from 'react';

/**
 * BODYMAX Engine Ring
 *
 * A glossy segmented ring in the BODYMAX logo's shape, drawn in the live accent
 * so it recolours with the theme. Seven segments, one per day of the training
 * week, with the wordmark and the week in the centre. Three behaviours:
 *   build  — segments rise from nothing (first program)
 *   week   — the finished week dims and the ring steps forward one notch
 *   change — segments rearrange into a new shape (program swap)
 */
const DAYS = 7;
const SIZE = 320;
const C = SIZE / 2;
const R_OUTER = 142;
const R_INNER = 104;
const GAP_DEG = 4;
const SWEEP = 360 / DAYS;

const polar = (r, deg) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [C + r * Math.cos(rad), C + r * Math.sin(rad)];
};

const segmentPath = (i, fill) => {
  const start = i * SWEEP + GAP_DEG / 2;
  const end = (i + 1) * SWEEP - GAP_DEG / 2;
  const rOut = R_INNER + (R_OUTER - R_INNER) * Math.max(fill, 0.02);
  const [x1, y1] = polar(R_INNER, start);
  const [x2, y2] = polar(R_INNER, end);
  const [x3, y3] = polar(rOut, end);
  const [x4, y4] = polar(rOut, start);
  return `M ${x1} ${y1} A ${R_INNER} ${R_INNER} 0 0 1 ${x2} ${y2} L ${x3} ${y3} A ${rOut} ${rOut} 0 0 0 ${x4} ${y4} Z`;
};

const FALLBACK = [0.4, 1, 0.62, 0.3, 0.86, 0.72, 0.38];
const REST = 0.12; // rest days still show a stub so the ring never looks broken

// Real session minutes -> segment heights. The longest session fills the band.
const shapeFromMinutes = (minutes) => {
  if (!Array.isArray(minutes) || minutes.length !== DAYS) return null;
  const peak = Math.max(...minutes.map(m => Number(m) || 0));
  if (peak <= 0) return null;
  return minutes.map(m => {
    const v = Number(m) || 0;
    return v <= 0 ? REST : Math.max(0.28, Math.min(1, v / peak));
  });
};

const rotate = (arr, n) => arr.slice(n).concat(arr.slice(0, n));

export const EngineRing = ({ mode = 'build', week = 1, minutes = null, size = 'min(80vw, 350px)' }) => {
  const shape = shapeFromMinutes(minutes) || FALLBACK;
  const frames = mode === 'complete'
    ? [shape.map(() => 1)]
    : mode === 'week'
      ? [shape, rotate(shape, 1)]
      : mode === 'change'
        ? [shape, rotate(shape, 3).map((v, i) => i % 2 ? Math.min(1, v + 0.18) : Math.max(REST, v - 0.14))]
        : [shape];
  const [step, setStep] = useState(0);
  const [entered, setEntered] = useState(false);
  const [flourish, setFlourish] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setEntered(true), 60);
    // One celebratory pulse once the completed ring has filled.
    const f = mode === 'complete' ? setTimeout(() => setFlourish(true), 1150) : null;
    if (frames.length < 2) return () => { clearTimeout(t); if (f) clearTimeout(f); };
    const id = setInterval(() => setStep(s => (s + 1) % frames.length), 2600);
    return () => { clearTimeout(t); clearInterval(id); if (f) clearTimeout(f); };
  }, [frames.length, mode]);

  const heights = frames[step];
  const rotation = mode === 'week' ? step * SWEEP : 0;
  const closing = mode === 'week' && step === 1 ? 6 : -1;

  return (
    <div
      className="relative flex items-center justify-center"
      style={{
        width: size,
        height: size,
        opacity: entered ? 1 : 0,
        transform: entered ? 'scale(1)' : 'scale(0.93)',
        transition: 'opacity 900ms cubic-bezier(0.22,1,0.36,1), transform 900ms cubic-bezier(0.22,1,0.36,1)',
        animation: flourish ? 'er-flourish 1200ms cubic-bezier(0.22,1,0.36,1) 1' : undefined,
      }}
      data-testid="engine-ring-visual"
    >
      {/* Outer halo, the way the logo glows off a black background */}
      <span
        className="absolute rounded-full"
        style={{
          inset: '2%',
          background: 'radial-gradient(circle, transparent 54%, rgba(var(--accent-rgb),0.34) 68%, rgba(var(--accent-rgb),0.06) 80%, transparent 90%)',
          filter: 'blur(10px)',
          animation: 'er-breathe 3400ms ease-in-out infinite',
        }}
      />

      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="relative w-full h-full" aria-hidden="true">
        <defs>
          {/* Metallic face: bright crown, mid body, dark foot */}
          <linearGradient id="er-face" x1="0.2" y1="0" x2="0.8" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
            <stop offset="18%" stopColor="var(--accent-hex)" stopOpacity="1" />
            <stop offset="72%" stopColor="var(--accent-hex)" stopOpacity="0.72" />
            <stop offset="100%" stopColor="#000000" stopOpacity="0.55" />
          </linearGradient>
          {/* Inner disc: brushed dark plate */}
          <radialGradient id="er-plate" cx="38%" cy="30%" r="80%">
            <stop offset="0%" stopColor="#242424" />
            <stop offset="55%" stopColor="#101010" />
            <stop offset="100%" stopColor="#050505" />
          </radialGradient>
          <linearGradient id="er-sheen" x1="0" y1="0" x2="0.3" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.5" />
            <stop offset="45%" stopColor="#ffffff" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
          <filter id="er-bloom" x="-35%" y="-35%" width="170%" height="170%">
            <feGaussianBlur stdDeviation="6" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Thin outer rim, like the logo's keyline */}
        <circle cx={C} cy={C} r={R_OUTER + 6} fill="none" stroke="var(--accent-hex)" strokeOpacity="0.55" strokeWidth="1.5" />
        <circle cx={C} cy={C} r={R_OUTER + 11} fill="none" stroke="var(--accent-hex)" strokeOpacity="0.16" strokeWidth="1" />

        {/* Empty track behind the segments */}
        {Array.from({ length: DAYS }).map((_, i) => (
          <path key={`t-${i}`} d={segmentPath(i, 1)} fill="#ffffff" opacity="0.05" />
        ))}

        <g
          style={{
            transform: `rotate(${rotation}deg)`,
            transformOrigin: '50% 50%',
            transition: 'transform 1100ms cubic-bezier(0.65,0,0.35,1)',
          }}
          filter="url(#er-bloom)"
        >
          {heights.map((h, i) => (
            <path
              key={`s-${i}`}
              d={segmentPath(i, entered ? h : 0.02)}
              fill="url(#er-face)"
              opacity={i === closing ? 0.2 : 1}
              style={{ transition: `d 1000ms cubic-bezier(0.22,1,0.36,1) ${i * 55}ms, opacity 700ms ease` }}
            />
          ))}
          {/* Shimmer: a bright pass travelling round the ring, one segment at a time */}
          {heights.map((h, i) => (
            <path
              key={`g-${i}`}
              d={segmentPath(i, entered ? h : 0.02)}
              fill="#ffffff"
              style={{
                mixBlendMode: 'overlay',
                transition: `d 1000ms cubic-bezier(0.22,1,0.36,1) ${i * 55}ms`,
                animation: `er-shimmer 2800ms ease-in-out ${i * 260}ms infinite`,
              }}
            />
          ))}
        </g>

        {/* Gloss sweep across the top half of the ring */}
        <g style={{ transformOrigin: '50% 50%', animation: 'er-spin 9s linear infinite' }}>
          <circle cx={C} cy={C} r={(R_OUTER + R_INNER) / 2} fill="none" stroke="url(#er-sheen)" strokeWidth={R_OUTER - R_INNER} />
        </g>

        {/* Centre plate */}
        <circle cx={C} cy={C} r={R_INNER - 4} fill="url(#er-plate)" />
        <circle cx={C} cy={C} r={R_INNER - 4} fill="none" stroke="var(--accent-hex)" strokeOpacity="0.3" strokeWidth="1" />
        <circle cx={C} cy={C} r={R_INNER - 12} fill="none" stroke="#ffffff" strokeOpacity="0.05" strokeWidth="1" />
      </svg>

      {/* Flourish: one ring of light pushes outward when the week completes */}
      {flourish && (
        <span
          className="absolute rounded-full pointer-events-none"
          style={{
            inset: '6%',
            border: '2px solid var(--accent-hex)',
            animation: 'er-pulse 1200ms cubic-bezier(0.22,1,0.36,1) 1 forwards',
          }}
        />
      )}

      <style>{`
        @keyframes er-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes er-shimmer { 0%, 70%, 100% { opacity: 0; } 18% { opacity: 0.30; } 40% { opacity: 0.06; } }
        @keyframes er-breathe { 0%, 100% { opacity: 0.75; transform: scale(1); } 50% { opacity: 1; transform: scale(1.035); } }
        @keyframes er-pulse { 0% { opacity: 0.85; transform: scale(0.92); } 100% { opacity: 0; transform: scale(1.22); } }
        @keyframes er-flourish { 0% { transform: scale(1); } 32% { transform: scale(1.055); } 100% { transform: scale(1); } }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="engine-ring-visual"] * { animation: none !important; }
        }
      `}</style>

      {/* Centre lockup: wordmark over the week, both deliberately restrained */}
      <div className="absolute flex flex-col items-center" style={{ opacity: entered ? 1 : 0, transition: 'opacity 900ms ease 260ms' }}>
        <span className="font-heading font-black tracking-tight text-white leading-none" style={{ fontSize: 'clamp(15px, 5.2vw, 22px)' }}>
          BODYMAX<span style={{ color: 'var(--accent-hex)' }}>.FIT</span>
        </span>
        <span
          className="font-heading font-bold uppercase tracking-[0.26em] mt-2"
          style={{ color: 'var(--accent-hex)', fontSize: 'clamp(10px, 3vw, 12px)' }}
          data-testid="engine-ring-week"
        >
          {mode === 'complete' ? `Week ${week} Complete` : mode === 'week' ? `Week ${week} → ${week + 1}` : `Week ${week}`}
        </span>
      </div>
    </div>
  );
};
